import { useMemo, useState } from "react";
import { IonContent, IonPage } from "@ionic/react";
import { useHistory } from "react-router-dom";

import { ActionBar } from "../components/ActionBar";
import { ChangeTodaySheet } from "../components/ChangeTodaySheet";
import { ExerciseRow } from "../components/ExerciseRow";
import { ExerciseSheet } from "../components/ExerciseSheet";
import { Hero } from "../components/Hero";
import { I } from "../components/icons";
import { Ring } from "../components/Ring";
import { RowGroup } from "../components/Rows";
import { Screen } from "../components/Shell";
import { TopBar } from "../components/TopBar";
import { moveById } from "../domain/moves";
import { dayLabel, todayStr } from "../domain/date";
import { sessionKcal } from "../domain/energy";
import { doseText, items, phase, sessionFor, sessionKeyFor, typeName, weekNo } from "../domain/plan";
import { isSteadyKey } from "../domain/templates";
import { buildQueue, dayQueue, queueMinutes, sessionPct } from "../domain/queue";
import { SESSION_LABEL } from "../domain/session";
import type { Exercise } from "../domain/types";
import { sessionProgress } from "../domain/week";
import { beep } from "../lib/device";
import { useCycleBar } from "../lib/useCycleBar";
import { useDeviceAnswers, useKaya, usePlayerMode } from "../store/kayaStore";
import { sessionGate } from "../domain/trainingQuestions";
import TrainSessionSteady from "./TrainSessionSteady";
import "./Train.css";

/** Today's session: a Steady template's own listing, with its reasons (ADR-052), or the Kaya one. */
export default function TrainSession() {
  // a held plan runs the Chair session, which is a Steady template, whatever the programme (P9)
  const answers = useDeviceAnswers();
  const steady = useKaya((s) => isSteadyKey(sessionGate(s.S, sessionKeyFor(s.S, todayStr()), answers).key));
  return steady ? <TrainSessionSteady /> : <TrainSessionKaya />;
}

/** Today's moves, block by block, with a dot per round done — Plan's listing on the v2 rows. */
function TrainSessionKaya() {
  const S = useKaya((s) => s.S);
  const answers = useDeviceAnswers();
  const pm = usePlayerMode();
  // One session at a time: with one open, Start goes back to it. (ADR-027)
  const session = useKaya((s) => s.session);
  const history = useHistory();
  const [changing, setChanging] = useState(false);
  const [shown, setShown] = useState<Exercise | null>(null);

  const today = todayStr();
  const gate = sessionGate(S, sessionKeyFor(S, today), answers);
  const key = gate.key;
  const s = sessionFor(key);
  const pct = sessionPct(S, today, key) ?? 0;
  const prog = sessionProgress(S, today, key);
  // Once a move is ticked the day lists what it froze (ADR-050): the phase and hip-safe it began
  // with. A frozen list that no longer matches the session shows today's moves without dots.
  const dq = useMemo(() => dayQueue(S, today, key), [S, today, key]);
  const p = dq.ran?.phase ?? phase(S);
  const SL = useMemo(
    () => (dq.ran && dq.ran.hipSafe !== !!S.profile.hipSafe ? { ...S, profile: { ...S.profile, hipSafe: dq.ran.hipSafe } } : S),
    [S, dq]
  );
  const queue = useMemo(() => dq.queue ?? buildQueue(SL, key, p), [dq, SL, key, p]);
  const done: Record<number, 1> = dq.queue ? (S.logs[today]?.done?.[key] ?? {}) : {};
  const resumeLabel = session ? `Resume ${SESSION_LABEL[session.kind].toLowerCase()}` : null;
  const rest = !s.blocks.length;

  const startSession = () => history.push(session ? session.route : "/workout", { from: history.location.pathname });
  const startWalk = () => {
    if (session) {
      history.push(session.route, { from: history.location.pathname });
      return;
    }
    // beeping here, inside the click, is also what unlocks the AudioContext for the player
    beep(880, 120, 0.15, S.profile.sound);
    history.push("/walk", { from: history.location.pathname });
  };

  const primary = gate.stop
    ? { label: "Start session", onClick: () => undefined, icon: I.play, disabled: true, reason: gate.line ?? undefined }
    : rest
    ? { label: resumeLabel ?? "Start a walk", onClick: startWalk, icon: I.walk }
    : {
        label:
          session && session.kind !== "workout"
            ? (resumeLabel ?? "Resume")
            : pct >= 1
              ? "Done — view"
              : pct > 0 && prog
                ? `Continue · ${prog.done} of ${prog.total}`
                : "Start session",
        onClick: startSession,
        icon: pct >= 1 ? I.check : I.play,
      };

  // the cycle line above Start, with its offer or Keep my session (ADR-057)
  const cycleBar = useCycleBar(today, queueMinutes(S, queue, pm.simple), startSession);

  return (
    <IonPage>
      <IonContent>
        <Screen>
          <div className="train-page">
            <TopBar title="Today’s session" back={{ to: "/train", label: "Back to Train" }} />

            <Hero
              label={`${dayLabel(today)} · week ${weekNo(S)}, phase ${p + 1}`}
              title={rest ? "Rest day" : typeName(key)}
              line={rest ? s.how : `${queueMinutes(S, queue, pm.simple)} min${prog ? ` · ${prog.total} moves` : ""}`}
              sub={rest ? "Optional: an easy 20-minute walk, or change today to a session." : `~${sessionKcal(S, key)} kcal est.`}
              visual={
                rest ? undefined : (
                  <Ring
                    pct={pct}
                    size={72}
                    stroke={7}
                    color="var(--brand)"
                    figure={prog ? `${prog.done}/${prog.total}` : null}
                  />
                )
              }
              visualSide
            />

            {rest ? null : (
              <p className="train-fine">{s.how} Tap any move for how-to and mistakes.</p>
            )}

            {s.blocks.map((b, bi) => {
              const its = items(SL, b);
              const R = b.rounds[p];
              const rm = SL.profile.restMove || b.rest[p];
              const rr = S.profile.restRound || b.restRound || 0;
              return (
                <RowGroup
                  key={`${b.title}:${bi}`}
                  heading={
                    <>
                      {b.title}
                      <span className="train-hsub">
                        {R > 1
                          ? `${R} rounds × ${its.length} moves · rest ${rm} s · ${rr} s between rounds`
                          : `${its.length} moves, once`}
                      </span>
                      {R > 1 ? (
                        <span className="train-hsub">
                          Do all {its.length} moves in order, rest {rr} s, then repeat — {R} times.
                          Dots show which rounds are done.
                        </span>
                      ) : null}
                    </>
                  }
                >
                  {its.map((it, ii) => {
                    const ex = moveById(it.id);
                    if (!ex) return null;
                    const idxs: number[] = [];
                    queue.forEach((q, qi) => {
                      if (q.kind !== "rest" && q.bi === bi && q.idx === ii + 1) idxs.push(qi);
                    });
                    const dn = idxs.filter((x) => done[x]).length;
                    const per = idxs.length / R;
                    const rounds = Array.from({ length: R }, (_, r) => idxs.slice(r * per, (r + 1) * per));
                    const roundsDone = rounds.filter((sl) => sl.every((x) => done[x])).length;
                    return (
                      <div className="train-ex" key={`${it.id}:${ii}`}>
                        <ExerciseRow
                          ex={ex}
                          dose={doseText(it, p)}
                          frac={idxs.length ? dn / idxs.length : 0}
                          hipSafe={SL.profile.hipSafe}
                          onOpen={setShown}
                        />
                        {R > 1 ? (
                          <span className="rdots" role="img" aria-label={`${roundsDone} of ${R} rounds done`}>
                            {rounds.map((sl, r) => (
                              <i key={r} className={sl.every((x) => done[x]) ? "on" : ""} />
                            ))}
                          </span>
                        ) : null}
                      </div>
                    );
                  })}
                </RowGroup>
              );
            })}

            <ActionBar
              primary={cycleBar.primary ?? primary}
              secondary={cycleBar.secondary ?? { label: "Change", onClick: () => setChanging(true) }}
              lead={gate.stop ? undefined : (gate.line ?? cycleBar.lead)}
            />
          </div>
        </Screen>
        <ChangeTodaySheet open={changing} onClose={() => setChanging(false)} ds={today} />
        {shown ? <ExerciseSheet ex={shown} hipSafe={S.profile.hipSafe} onClose={() => setShown(null)} /> : null}
      </IonContent>
    </IonPage>
  );
}
